/**
 * @description
 * InvoicesComponent provides an interface for searching, filtering, and consulting
 * invoiced sales, tracking payment settlement states (unpaid, partially paid, paid),
 * inspecting split receivables (Patient, AMO, AMC), and viewing/downloading sealed PDF duplicates.
 *
 * Called via route: `/sales/invoices`
 *
 * Dependencies:
 * - InvoicesService (`@app/sales/invoices/invoices.service`)
 * - AlertService (`@app/shared/alert.service`)
 * - SpinnerService (`@app/shared/spinner.service`)
 * - SearchPartnerComponent (`@app/shared/search-partner/search-partner.component`)
 * - SelectDateComponent (`@app/shared/select-date/select-date.component`)
 * - MenuCommonComponent (`@app/shared/menu-common/menu-common.component`)
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { catchError, finalize, of } from 'rxjs';
import Swal from 'sweetalert2';

import { AlertService } from '@app/shared/alert.service';
import { isSealedOrder } from '@app/shared/constants';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { PrintService } from '@app/shared/print.service';
import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { SpinnerService } from '@app/shared/spinner.service';
import { C_value, Partner } from '@app/shared/_models/common';
import {
  DeletteringPayload,
  DeletteringResult,
  InvoicedSale,
  InvoicesQueryParams,
  ReceivablePaymentAllocation,
  SalesReceivableSummary
} from '@app/shared/_models/sales';
import { BankLetteringService } from '@app/sales/bank-lettering/bank-lettering.service';

import { InvoicesService } from './invoices.service';

/**
 * Filter configuration interface for the invoice search view.
 */
export interface InvoiceSearchFilter {
  /** Partner ID (customer or bill-to) */
  partnerId: number | null;
  /** Start date filter */
  dateFrom?: Date;
  /** End date filter */
  dateTo?: Date;
  /** Payment status filter ('ALL' | 'OPEN' | 'PARTIALLY_PAID' | 'PAID') */
  state: string;
  /** Document type filter ('ALL' | 'FACTURE' | 'FACTURE_ACOMPTE') */
  docType: string;
  /** Keyword search filter */
  search: string;
  /** Results page size limit */
  limit: number;
}

@Component({
  selector: 'app-invoices',
  standalone: true,
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDividerModule,
    MatCardModule,
    MatMenuModule,
    MenuCommonComponent,
    SearchPartnerComponent,
    SelectDateComponent
  ]
})
export class InvoicesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private invoicesService = inject(InvoicesService);
  private bankLetteringService = inject(BankLetteringService);
  private alertService = inject(AlertService);
  private spinner = inject(SpinnerService);
  private printService = inject(PrintService);

  /** List of fetched invoiced sales */
  public invoices: InvoicedSale[] = [];

  /** Active payment methods list for human-readable labels */
  public paymentMethods: C_value[] = [];

  /** Map of loaded allocations by receivable ID */
  public receivableAllocations = new Map<number, ReceivablePaymentAllocation[]>();

  /** Set of receivable IDs currently loading allocations */
  public loadingAllocations = new Set<number>();

  /** Set of receivable IDs whose allocations drawer is expanded */
  public expandedReceivableAllocations = new Set<number>();

  /** Loading state indicator */
  public isLoading: boolean = false;

  /** Set of expanded order IDs to display nested receivables */
  public expandedInvoiceOrderIds = new Set<number>();

  /** Selected customer partner for filtering */
  public selectedPartner: Partner | null = null;

  /** Available options for result limits */
  public readonly limitOptions: number[] = [25, 50, 100, 200];

  /** Default days offset for initial date range (90 days) */
  public readonly defaultDaysOffset: number = 90;

  /** Active search filter model */
  public filter: InvoiceSearchFilter = {
    partnerId: null,
    dateFrom: (() => {
      const d = new Date();
      d.setDate(d.getDate() - this.defaultDaysOffset);
      return d;
    })(),
    dateTo: new Date(),
    state: 'ALL',
    docType: 'ALL',
    search: '',
    limit: 50
  };

  /** Summary KPI aggregates computed from the loaded dataset */
  public totalInvoicedTtc: number = 0;
  public totalPaid: number = 0;
  public totalDue: number = 0;
  public totalPatientDue: number = 0;
  public totalTpDue: number = 0;
  public invoiceCount: number = 0;

  /** Columns displayed in the main invoices table */
  public displayedColumns: string[] = [
    'expand',
    'docRef',
    'invoiceDate',
    'customer',
    'origin',
    'amountTaxx',
    'amountTaxi',
    'amountPaid',
    'amountDue',
    'paymentState',
    'actions'
  ];

  ngOnInit(): void {
    this.alertService.clear();
    this.bankLetteringService
      .getPaymentMethods()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((methods) => {
        this.paymentMethods = methods || [];
        this.cdr.markForCheck();
      });
    this.loadInvoices();
  }

  /**
   * Loads invoiced sales from the backend matching current filter criteria.
   */
  public loadInvoices(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    const queryParams: InvoicesQueryParams = {
      partnerId: this.filter.partnerId,
      dateFrom: this.filter.dateFrom ? this.formatDateForApi(this.filter.dateFrom, false) : null,
      dateTo: this.filter.dateTo ? this.formatDateForApi(this.filter.dateTo, true) : null,
      state: this.filter.state !== 'ALL' ? this.filter.state : null,
      docType: this.filter.docType !== 'ALL' ? this.filter.docType : null,
      search: this.filter.search ? this.filter.search.trim() : null,
      limit: this.filter.limit,
      offset: 0
    };

    this.invoicesService
      .getInvoices(queryParams)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          const serverMsg = err.error?.message || err.error || err.message || 'Erreur serveur inconnue';
          Swal.fire({
            title: 'Erreur',
            text: `Impossible de charger les factures.\nDétails : ${serverMsg}`,
            icon: 'error',
            confirmButtonText: 'Fermer'
          });
          return of([] as InvoicedSale[]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe((data: InvoicedSale[]) => {
        this.invoices = data || [];
        this.computeSummaryKpis();
        this.cdr.markForCheck();
      });
  }

  /**
   * Computes KPI totals (Total TTC, Total Paid, Total Due) from the current invoices list.
   */
  private computeSummaryKpis(): void {
    this.invoiceCount = this.invoices.length;
    this.totalInvoicedTtc = this.invoices.reduce((sum, inv) => sum + Number(inv.amountTaxi || 0), 0);
    this.totalPaid = this.invoices.reduce((sum, inv) => sum + Number(inv.amountPaid || 0), 0);
    this.totalDue = this.invoices.reduce((sum, inv) => sum + Number(inv.amountDue || 0), 0);
    this.totalPatientDue = this.invoices.reduce((sum, inv) => sum + this.getPatientDue(inv), 0);
    this.totalTpDue = this.invoices.reduce((sum, inv) => sum + this.getTpDue(inv), 0);
  }

  /**
   * Formats a Date object into a YYYY-MM-DD string for API queries.
   *
   * @param date Date object to format.
   * @param isEndOfDay If true, formats end of day boundaries.
   * @returns Formatted date string.
   */
  private formatDateForApi(date: Date, isEndOfDay: boolean): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return isEndOfDay ? `${year}-${month}-${day}T23:59:59Z` : `${year}-${month}-${day}T00:00:00Z`;
  }

  /**
   * Handles partner selection change from the partner search autocomplete.
   *
   * @param partner Selected Partner object or null when cleared.
   */
  public onPartnerChange(partner: Partner | null): void {
    this.selectedPartner = partner;
    this.filter.partnerId = partner && partner.id > 0 ? partner.id : null;
    this.loadInvoices();
  }

  /**
   * Handles date picker changes for start and end boundaries.
   *
   * @param date New date selected.
   * @param field Boundary identifier ('from' or 'to').
   */
  public onDateChange(date: Date, field: 'from' | 'to'): void {
    if (field === 'from') {
      this.filter.dateFrom = date;
    } else {
      this.filter.dateTo = date;
    }
    this.loadInvoices();
  }

  /**
   * Triggers a new search query.
   */
  public onSearch(): void {
    this.loadInvoices();
  }

  /**
   * Resets all search filters back to their default values and reloads data.
   */
  public onResetFilters(): void {
    const defaultDateFrom = new Date();
    defaultDateFrom.setDate(defaultDateFrom.getDate() - this.defaultDaysOffset);

    this.selectedPartner = null;
    this.filter = {
      partnerId: null,
      dateFrom: defaultDateFrom,
      dateTo: new Date(),
      state: 'ALL',
      docType: 'ALL',
      search: '',
      limit: 50
    };
    this.expandedInvoiceOrderIds.clear();
    this.loadInvoices();
  }

  /**
   * Toggles the expanded view of nested receivables for a specific invoice.
   *
   * @param orderId Sales order identifier.
   */
  public toggleExpand(orderId: number): void {
    if (this.expandedInvoiceOrderIds.has(orderId)) {
      this.expandedInvoiceOrderIds.delete(orderId);
    } else {
      this.expandedInvoiceOrderIds.add(orderId);
    }
    this.cdr.markForCheck();
  }

  /**
   * Checks whether the receivables drawer is expanded for a given order ID.
   *
   * @param orderId Sales order identifier.
   * @returns True if expanded, false otherwise.
   */
  public isExpanded(orderId: number): boolean {
    return this.expandedInvoiceOrderIds.has(orderId);
  }

  // Même URL que « Imprimer la facture » du devis : PDF rendu depuis la pièce scellée, chaque ouverture comptée au JET (doc front.sales.invoices).
  public onViewInvoice(invoice: InvoicedSale): void {
    this.invoicesService.openPdf(this.printService.getOrderJustificatif$(invoice.orderId, 'PRINT_INVOICE_A4', invoice.docRef));
  }

  // Facture reprise d'orthop (état MIGRATION) : la reprise ne produit aucun PDF, on n'en propose donc pas.
  public isMigrated(invoice: InvoicedSale): boolean {
    return isSealedOrder(invoice.orderState);
  }

  /**
   * Returns true if the invoice represents a down payment invoice (FACTURE_ACOMPTE).
   *
   * @param invoice InvoicedSale instance.
   * @returns True if a deposit invoice.
   */
  public isDepositInvoice(invoice: InvoicedSale): boolean {
    return invoice.docType === 'FACTURE_ACOMPTE' || (invoice.docRef ? invoice.docRef.startsWith('FAC-AC') : false);
  }

  /**
   * Returns true if the invoice is an FSE (Facture SESAM-Vitale).
   */
  public isFseInvoice(invoice: InvoicedSale): boolean {
    return invoice.docType === 'FACTURE_FSE' || (invoice.docRef ? invoice.docRef.startsWith('FSE-') : false);
  }

  /**
   * Returns true if the invoice has third-party healthcare receivables (AMO / AMC).
   */
  public hasThirdParty(invoice: InvoicedSale): boolean {
    return this.isFseInvoice(invoice) || (invoice.receivables || []).some((r) => r.payerType === 'AMO' || r.payerType === 'AMC');
  }

  /**
   * Returns the patient / insured receivable (RAC).
   */
  public getPatientReceivable(invoice: InvoicedSale): SalesReceivableSummary | undefined {
    return (invoice.receivables || []).find((r) => r.payerType === 'CLIENT' || r.payerId === invoice.customerId);
  }

  /**
   * Returns patient expected amount (Reste à charge attendu).
   */
  public getPatientExpected(invoice: InvoicedSale): number {
    const rec = this.getPatientReceivable(invoice);
    return rec ? Number(rec.amountExpected || 0) : Number(invoice.amountTaxi || 0);
  }

  /**
   * Returns patient paid amount.
   */
  public getPatientPaid(invoice: InvoicedSale): number {
    const rec = this.getPatientReceivable(invoice);
    return rec ? Number(rec.amountPaid || 0) : 0;
  }

  /**
   * Returns patient due amount (Reste à charge patient dû).
   */
  public getPatientDue(invoice: InvoicedSale): number {
    const rec = this.getPatientReceivable(invoice);
    return rec ? Math.max(0, Number(rec.amountDue || 0)) : Number(invoice.amountDue || 0);
  }

  /**
   * Returns third-party (AMO + AMC) receivables.
   */
  public getTpReceivables(invoice: InvoicedSale): SalesReceivableSummary[] {
    return (invoice.receivables || []).filter((r) => r.payerType === 'AMO' || r.payerType === 'AMC');
  }

  /**
   * Returns total third-party expected amount.
   */
  public getTpExpected(invoice: InvoicedSale): number {
    return this.getTpReceivables(invoice).reduce((sum, r) => sum + Number(r.amountExpected || 0), 0);
  }

  /**
   * Returns total third-party paid amount (virements RSP lettrés).
   */
  public getTpPaid(invoice: InvoicedSale): number {
    return this.getTpReceivables(invoice).reduce((sum, r) => sum + Number(r.amountPaid || 0), 0);
  }

  /**
   * Returns total third-party due amount.
   */
  public getTpDue(invoice: InvoicedSale): number {
    return this.getTpReceivables(invoice).reduce((sum, r) => sum + Math.max(0, Number(r.amountDue || 0)), 0);
  }

  /**
   * Returns visual badge CSS class for payer type in the drawer subtable.
   */
  public getPayerTypeBadgeClass(payerType?: string): string {
    switch ((payerType || '').toUpperCase()) {
      case 'AMO':
        return 'badge-payer-amo';
      case 'AMC':
        return 'badge-payer-amc';
      case 'CLIENT':
      default:
        return 'badge-payer-client';
    }
  }

  /**
   * Returns visual badge properties specialized for FSE / third-party invoices.
   */
  public getInvoicePaymentStateBadge(invoice: InvoicedSale): { cssClass: string; label: string; icon: string } {
    if (this.hasThirdParty(invoice)) {
      const patientDue = this.getPatientDue(invoice);
      const tpDue = this.getTpDue(invoice);
      if (patientDue <= 0 && tpDue <= 0) {
        return {
          cssClass: 'badge-paid',
          label: 'Soldée',
          icon: 'check_circle'
        };
      }
      if (patientDue <= 0 && tpDue > 0) {
        return {
          cssClass: 'badge-rac-paid',
          label: 'RAC Réglé (Attente TP)',
          icon: 'hourglass_empty'
        };
      }
      if (patientDue > 0 && tpDue <= 0) {
        return {
          cssClass: 'badge-partial',
          label: 'Attente Patient',
          icon: 'timelapse'
        };
      }
      return {
        cssClass: 'badge-open',
        label: `À régler (RAC : ${patientDue.toFixed(2)} €)`,
        icon: 'error_outline'
      };
    }
    return this.getPaymentStateBadge(invoice.paymentState);
  }

  /**
   * Returns visual badge properties (CSS class, label text, icon) for a payment state.
   *
   * @param state Payment state ('OPEN' | 'PARTIALLY_PAID' | 'PAID').
   * @returns Object containing badge styling and display details.
   */
  public getPaymentStateBadge(state: string): { cssClass: string; label: string; icon: string } {
    switch ((state || '').toUpperCase()) {
      case 'PAID':
        return {
          cssClass: 'badge-paid',
          label: 'Soldée',
          icon: 'check_circle'
        };
      case 'PARTIALLY_PAID':
        return {
          cssClass: 'badge-partial',
          label: 'Partiellement réglée',
          icon: 'timelapse'
        };
      case 'OPEN':
      default:
        return {
          cssClass: 'badge-open',
          label: 'Non réglée',
          icon: 'error_outline'
        };
    }
  }

  /**
   * Returns visual badge styling for individual receivable states.
   *
   * @param state Receivable state string.
   * @returns Badge CSS class name.
   */
  public getReceivableStateClass(state: string): string {
    switch ((state || '').toUpperCase()) {
      case 'PAID':
        return 'badge-paid';
      case 'PARTIALLY_PAID':
        return 'badge-partial';
      default:
        return 'badge-open';
    }
  }

  /**
   * TrackBy function for invoice list rendering optimization.
   *
   * @param index Item index.
   * @param item InvoicedSale item.
   * @returns Unique identifier string.
   */
  public trackByInvoice(index: number, item: InvoicedSale): string {
    return `${item.orderId}-${item.deliveryId || 0}-${item.docRef}`;
  }

  /**
   * TrackBy function for receivable rows rendering optimization.
   *
   * @param index Item index.
   * @param item SalesReceivableSummary item.
   * @returns Unique receivable ID.
   */
  public trackByReceivable(index: number, item: SalesReceivableSummary): number {
    return item.id;
  }

  /**
   * Returns human-readable label for a payment method ID.
   *
   * @param methodId Payment method ID.
   * @returns Formatted payment method label.
   */
  public getPaymentMethodName(methodId: number): string {
    const method = this.paymentMethods.find((m) => m.id === methodId);
    return method?.name || `Mode #${methodId}`;
  }

  /**
   * Checks whether the allocations drawer is expanded for a given receivable ID.
   *
   * @param receivableId Target receivable ID.
   * @returns True if expanded.
   */
  public isReceivableAllocationsExpanded(receivableId: number): boolean {
    return this.expandedReceivableAllocations.has(receivableId);
  }

  /**
   * Checks whether allocations are currently loading for a given receivable ID.
   *
   * @param receivableId Target receivable ID.
   * @returns True if loading.
   */
  public isLoadingAllocations(receivableId: number): boolean {
    return this.loadingAllocations.has(receivableId);
  }

  /**
   * Retrieves the cached list of loaded allocations for a receivable.
   *
   * @param receivableId Target receivable ID.
   * @returns Array of active allocations.
   */
  public getReceivableAllocationsList(receivableId: number): ReceivablePaymentAllocation[] {
    return this.receivableAllocations.get(receivableId) || [];
  }

  /**
   * Toggles the display of payment allocations for a receivable and loads them if not already fetched.
   *
   * @param receivable Target receivable summary.
   */
  public toggleReceivableAllocations(receivable: SalesReceivableSummary): void {
    if (this.expandedReceivableAllocations.has(receivable.id)) {
      this.expandedReceivableAllocations.delete(receivable.id);
      this.cdr.markForCheck();
      return;
    }

    this.expandedReceivableAllocations.add(receivable.id);
    this.fetchReceivableAllocations(receivable.id);
  }

  /**
   * Fetches active payment allocations for a receivable from the server.
   *
   * @param receivableId Target receivable ID.
   */
  public fetchReceivableAllocations(receivableId: number): void {
    this.loadingAllocations.add(receivableId);
    this.cdr.markForCheck();

    this.bankLetteringService
      .getReceivableAllocations(receivableId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          const msg = err.error?.message || err.message || 'Erreur lors du chargement des règlements';
          this.alertService.error(msg);
          return of([] as ReceivablePaymentAllocation[]);
        }),
        finalize(() => {
          this.loadingAllocations.delete(receivableId);
          this.cdr.markForCheck();
        })
      )
      .subscribe((allocs) => {
        this.receivableAllocations.set(receivableId, allocs || []);
        this.cdr.markForCheck();
      });
  }

  /**
   * Prompts user confirmation to de-letter an active payment allocation,
   * collecting mandatory audit reason and optional partial amount, and dispatches EVENTTYP_SALES_DELETTERING.
   *
   * @param invoice Target invoiced sale.
   * @param receivable Target receivable summary.
   * @param allocation Target payment allocation to de-letter.
   */
  public onDeletterAllocation(
    invoice: InvoicedSale,
    receivable: SalesReceivableSummary,
    allocation: ReceivablePaymentAllocation
  ): void {
    const defaultAmount = allocation.allocated_amount;
    const maxAmount = allocation.allocated_amount;
    const methodName = this.getPaymentMethodName(allocation.payment_method);
    const bankRefText = allocation.bank_reference ? ` (Réf: ${allocation.bank_reference})` : '';

    Swal.fire({
      title: 'Délettrage d\'imputation',
      html: `
        <div style="text-align: left; font-size: 13.5px; line-height: 1.5; color: #334155;">
          <p>Vous vous apprêtez à annuler l'imputation de <strong>${defaultAmount.toFixed(2)} €</strong> (${methodName}${bankRefText}) sur la créance de <strong>${receivable.payerName || 'Client'}</strong> (Facture <strong>${invoice.docRef}</strong>).</p>
          <div style="background-color: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; padding: 10px; margin: 12px 0; color: #991b1b; font-size: 12.5px;">
            <strong>Exigence BOFIP ISCA (art. 286-I-3° bis CGI) :</strong><br>
            Cette opération enregistre une contre-passation inaltérable cryptographiquement scellée. Le règlement bancaire reste intact et son solde sera réouvert.
          </div>
          <label style="display: block; font-weight: 600; margin-top: 10px; margin-bottom: 4px;">Motif d'audit obligatoire (min. 3 caractères) :</label>
          <input id="swal-deletter-reason" class="swal2-input" style="margin: 0 0 10px 0; width: 100%; box-sizing: border-box;" placeholder="Ex: Erreur d'affectation client, contestation...">
          <label style="display: block; font-weight: 600; margin-top: 8px; margin-bottom: 4px;">Montant à délettrer (€) :</label>
          <input id="swal-deletter-amount" type="number" step="0.01" min="0.01" max="${maxAmount}" value="${defaultAmount.toFixed(2)}" class="swal2-input" style="margin: 0; width: 100%; box-sizing: border-box;">
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmer le délettrage',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc2626',
      focusConfirm: false,
      preConfirm: () => {
        const reasonInput = document.getElementById('swal-deletter-reason') as HTMLInputElement;
        const amountInput = document.getElementById('swal-deletter-amount') as HTMLInputElement;

        const reason = reasonInput?.value?.trim() || '';
        const amount = parseFloat(amountInput?.value || '0');

        if (reason.length < 3) {
          Swal.showValidationMessage('Le motif d\'audit est obligatoire (au moins 3 caractères).');
          return false;
        }

        if (isNaN(amount) || amount <= 0) {
          Swal.showValidationMessage('Veuillez saisir un montant positif valide.');
          return false;
        }

        if (amount > maxAmount + 0.0001) {
          Swal.showValidationMessage(`Le montant ne peut pas dépasser le montant imputé (${maxAmount.toFixed(2)} €).`);
          return false;
        }

        return { reason, amount };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const payload: DeletteringPayload = {
          receivable_id: receivable.id,
          payment_id: allocation.payment_id,
          amount: result.value.amount,
          reason: result.value.reason
        };

        this.executeDelettering(receivable.id, payload);
      }
    });
  }

  /**
   * Executes the delettering call via the service and updates the view upon completion.
   */
  private executeDelettering(receivableId: number, payload: DeletteringPayload): void {
    this.spinner.show();

    this.bankLetteringService
      .deletterAllocation(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response) => {
          const res = response?.data as DeletteringResult;
          Swal.fire({
            title: 'Délettrage réussi',
            html: `
              <div style="font-size: 13.5px; text-align: left; color: #334155;">
                <p>La contre-passation d'imputation de <strong>${(res?.unlettered_amount || payload.amount || 0).toFixed(2)} €</strong> a été scellée avec succès.</p>
                <ul style="padding-left: 20px; margin-top: 8px;">
                  <li>Statut de la créance : <strong>${res?.new_receivable_state || 'Ouverte'}</strong></li>
                  <li>Statut de la commande : <strong>${res?.new_order_state || 'Facturée'}</strong></li>
                  <li>Solde libéré sur le règlement : <strong>${(res?.remaining_unallocated_payment || 0).toFixed(2)} €</strong></li>
                </ul>
              </div>
            `,
            icon: 'success',
            confirmButtonText: 'Fermer'
          });

          // Refresh allocations for this receivable and reload full invoice list
          this.fetchReceivableAllocations(receivableId);
          this.loadInvoices();
        },
        error: (err) => {
          const msg = err.error?.message || err.error || err.message || 'Erreur lors du délettrage';
          Swal.fire({
            title: 'Échec du délettrage',
            text: msg,
            icon: 'error',
            confirmButtonText: 'Fermer'
          });
        }
      });
  }
}
